import { useState } from "react";
import { View, Text, TextInput, Pressable, ActivityIndicator } from "react-native";
import { useRouter } from "expo-router";
import { useAuth } from "@/contexts/AuthContext";
import { useClub } from "@/contexts/ClubContext";
import { createClub } from "@/lib/services";
import { colors, radius } from "@/lib/theme";
import ContentWidth from "@/components/ContentWidth";
import KeyboardAwareScroll from "@/components/KeyboardAwareScroll";

export default function CreateClubScreen() {
  const router = useRouter();
  const { user } = useAuth();
  const { switchClub } = useClub();

  const [name, setName] = useState("");
  const [shortName, setShortName] = useState("");
  const [teams, setTeams] = useState<string[]>(["1st XI", "2nd XI"]);
  const [newTeam, setNewTeam] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const addTeam = () => {
    const trimmed = newTeam.trim();
    if (!trimmed || teams.includes(trimmed)) { setNewTeam(""); return; }
    setTeams([...teams, trimmed]);
    setNewTeam("");
  };

  const handleCreate = async () => {
    if (!name.trim()) { setError("Club name is required."); return; }
    if (!user) return;
    setLoading(true);
    setError("");
    try {
      const finalShortName = shortName.trim() || name.trim().split(/\s+/).map((w) => w[0]).join("").toUpperCase();
      const clubId = await createClub({
        name: name.trim(),
        shortName: finalShortName,
        teams: teams.length > 0 ? teams : ["1st XI"],
        userId: user.uid,
        userEmail: user.email || "",
        userName: user.displayName || user.email?.split("@")[0] || "",
      });
      switchClub(clubId);
      router.back();
    } catch (err: any) {
      setError(err.message || "Something went wrong.");
      setLoading(false);
    }
  };

  return (
    <KeyboardAwareScroll style={{ flex: 1, backgroundColor: colors.bg }}>
      <ContentWidth>
        <View style={{ padding: 20 }}>
          <Text style={{ fontSize: 22, fontWeight: "800", color: colors.ink, marginBottom: 8 }}>Create a new club</Text>
          <Text style={{ fontSize: 14, color: colors.inkMuted, marginBottom: 24, lineHeight: 20 }}>
            You'll be the admin. You can invite members and set up teams after.
          </Text>

          <Text style={{ fontSize: 12, fontWeight: "800", color: colors.inkMuted, textTransform: "uppercase", letterSpacing: 1, marginBottom: 8 }}>Club name</Text>
          <TextInput
            value={name}
            onChangeText={setName}
            placeholder="e.g. Peterborough Town CC"
            placeholderTextColor={colors.inkMuted}
            style={{
              backgroundColor: colors.white, borderRadius: radius.md, borderWidth: 1, borderColor: colors.line,
              paddingHorizontal: 14, paddingVertical: 12, fontSize: 16, color: colors.ink,
            }}
          />

          <Text style={{ fontSize: 12, fontWeight: "800", color: colors.inkMuted, textTransform: "uppercase", letterSpacing: 1, marginBottom: 8, marginTop: 20 }}>Short name (optional)</Text>
          <TextInput
            value={shortName}
            onChangeText={setShortName}
            placeholder="e.g. PTCC"
            placeholderTextColor={colors.inkMuted}
            style={{
              backgroundColor: colors.white, borderRadius: radius.md, borderWidth: 1, borderColor: colors.line,
              paddingHorizontal: 14, paddingVertical: 12, fontSize: 16, color: colors.ink,
            }}
          />
          <Text style={{ fontSize: 12, color: colors.inkMuted, marginTop: 4 }}>
            Shown in the header. Auto-generated if left empty.
          </Text>

          <Text style={{ fontSize: 12, fontWeight: "800", color: colors.inkMuted, textTransform: "uppercase", letterSpacing: 1, marginBottom: 8, marginTop: 20 }}>Teams</Text>
          <View style={{ flexDirection: "row", flexWrap: "wrap", gap: 8 }}>
            {teams.map((t, i) => (
              <Pressable
                key={t}
                onPress={() => setTeams(teams.filter((_, idx) => idx !== i))}
                style={{
                  flexDirection: "row", alignItems: "center", gap: 6,
                  backgroundColor: colors.brand50, borderWidth: 1, borderColor: colors.brandLight,
                  borderRadius: 999, paddingHorizontal: 14, paddingVertical: 8,
                }}
              >
                <Text style={{ fontSize: 14, fontWeight: "600", color: colors.brandDark }}>{t}</Text>
                <Text style={{ fontSize: 16, color: colors.inkMuted, fontWeight: "700" }}>×</Text>
              </Pressable>
            ))}
          </View>
          <View style={{ flexDirection: "row", gap: 8, marginTop: 10 }}>
            <TextInput
              value={newTeam}
              onChangeText={setNewTeam}
              placeholder="Add a team"
              placeholderTextColor={colors.inkMuted}
              onSubmitEditing={addTeam}
              returnKeyType="done"
              style={{
                flex: 1, backgroundColor: colors.white, borderRadius: radius.md, borderWidth: 1, borderColor: colors.line,
                paddingHorizontal: 14, paddingVertical: 10, fontSize: 15, color: colors.ink,
              }}
            />
            <Pressable onPress={addTeam} style={({ pressed }) => ({ justifyContent: "center", paddingHorizontal: 16, opacity: pressed ? 0.7 : 1 })}>
              <Text style={{ fontSize: 14, fontWeight: "700", color: colors.brand }}>Add</Text>
            </Pressable>
          </View>
          <Text style={{ fontSize: 12, color: colors.inkMuted, marginTop: 6 }}>
            You can also import teams from Play-Cricket later.
          </Text>

          {error ? <Text style={{ color: colors.danger, fontSize: 14, fontWeight: "600", marginTop: 16 }}>{error}</Text> : null}

          <Pressable
            onPress={handleCreate}
            disabled={loading}
            style={({ pressed }) => ({
              backgroundColor: colors.brand, borderRadius: radius.lg, padding: 16,
              alignItems: "center", marginTop: 24, opacity: pressed || loading ? 0.8 : 1,
            })}
          >
            {loading ? <ActivityIndicator color="#fff" /> : <Text style={{ color: "#fff", fontSize: 16, fontWeight: "700" }}>Create club</Text>}
          </Pressable>
        </View>
        <View style={{ height: 40 }} />
      </ContentWidth>
    </KeyboardAwareScroll>
  );
}
